import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms and Conditions | Acadly" },
      {
        name: "description",
        content:
          "The terms that govern your use of Acadly.in, covering content ownership, acceptable use, third-party services and limits of liability.",
      },
      { property: "og:title", content: "Terms and Conditions | Acadly" },
      {
        property: "og:description",
        content:
          "Read the full terms and conditions that apply when you use Acadly.in.",
      },
      { property: "og:url", content: "https://acadly.in/terms" },
    ],
    links: [{ rel: "canonical", href: "https://acadly.in/terms" }],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <div className="mx-auto max-w-3xl px-5 py-16 md:py-20 prose-post">
      <div className="stamp mb-6">Legal · Terms</div>
      <h1
        className="text-4xl md:text-5xl font-semibold"
        style={{ fontFamily: "var(--font-display)" }}
      >
        Terms and Conditions
      </h1>
      <p className="mono-label !mt-2">Last updated: 25 July 2026</p>

      <p>
        These Terms and Conditions (the &quot;Terms&quot;) form a legal
        agreement between you and <strong>Acadly.in</strong> (referred to as
        &quot;Acadly&quot;, &quot;we&quot;, &quot;us&quot; or &quot;our&quot;)
        and govern your access to and use of the website at{" "}
        <a href="https://acadly.in">acadly.in</a> and any related pages,
        content and services made available on it (together, the
        &quot;Site&quot;). By accessing or using the Site, you confirm that
        you have read, understood and agreed to these Terms. If you do not
        agree with any part of them, please stop using the Site.
      </p>

      <h2>1. About Acadly</h2>
      <p>
        Acadly is an independent editorial website that publishes study notes,
        subject explainers and study-strategy articles written primarily for
        Indian college students. It is operated by Dhairya from New Delhi,
        India. Acadly is not a coaching institute, an educational board, or
        any form of degree-awarding body, and it does not offer paid courses,
        tutoring, admissions counselling or examination material.
      </p>

      <h2>2. Eligibility</h2>
      <p>
        You must be at least thirteen years old to use the Site. If you are
        below the age of majority in your jurisdiction, you may use the Site
        only under the supervision of a parent or legal guardian who agrees
        to be bound by these Terms on your behalf.
      </p>

      <h2>3. Nature of the content</h2>
      <p>
        The content on the Site is provided for general educational and
        informational purposes. It reflects the author&apos;s understanding at
        the time of writing and is not a substitute for professional academic,
        career, financial, legal or medical advice. You should not act on any
        information on the Site without verifying it against a current
        authoritative source that applies to your situation.
      </p>

      <h2>4. Intellectual property</h2>
      <p>
        All content on the Site, including the articles, headlines,
        illustrations, layout, visual design, source code and the Acadly name
        and logo, is the property of Acadly or its licensors and is protected
        under Indian and international copyright, trademark and other
        applicable laws. All rights not expressly granted in these Terms are
        reserved.
      </p>

      <h3>4.1 What you may do</h3>
      <ul>
        <li>
          Read, save and print articles for your own personal,
          non-commercial study.
        </li>
        <li>Share direct links to articles on the Site.</li>
        <li>
          Quote short extracts, up to a few sentences, in your own work,
          provided you credit Acadly clearly and link back to the original
          article.
        </li>
      </ul>

      <h3>4.2 What you may not do</h3>
      <ul>
        <li>
          Republish, mirror or redistribute full articles on another website,
          newsletter, application, publication or printed material without
          our prior written permission.
        </li>
        <li>
          Modify an article and present it as your own, or as an
          unattributed piece of general information.
        </li>
        <li>
          Use Site content, in whole or in part, to train, fine-tune or
          evaluate a machine-learning model that will be commercialised or
          distributed to third parties.
        </li>
        <li>
          Remove, obscure or alter any copyright, trademark or attribution
          notices on the Site.
        </li>
      </ul>
      <p>
        For any use beyond the permissions above, please write to{" "}
        <a href="mailto:hello@acadly.in">hello@acadly.in</a> with details of
        the intended use.
      </p>

      <h2>5. Acceptable use</h2>
      <p>You agree that you will not:</p>
      <ul>
        <li>
          Attempt to break, probe or interfere with the Site, its
          infrastructure or its security features.
        </li>
        <li>
          Use automated tools to scrape or index the Site in a way that
          degrades performance for other readers, or in violation of the
          instructions in our robots.txt file.
        </li>
        <li>
          Impersonate any person or entity when writing to us or interacting
          with the Site.
        </li>
        <li>
          Upload or transmit any material that is unlawful, defamatory,
          harassing, obscene or that infringes the rights of any third party.
        </li>
        <li>
          Use the Site for any purpose that violates applicable law or these
          Terms.
        </li>
      </ul>

      <h2>6. Third-party links and services</h2>
      <p>
        Articles on the Site link to third-party websites and reference
        materials. Acadly does not control those websites and is not
        responsible for their content, policies, availability or practices.
        Following an external link is at your own discretion, and the terms
        and privacy notice of the destination website will apply to your
        interaction with it.
      </p>

      <h2>7. Advertising</h2>
      <p>
        The Site does not currently display advertising. If advertising is
        introduced in future, including through Google AdSense or other
        reputable networks, this section will be updated, and details of how
        advertising cookies operate and how to opt out of personalised
        advertising will be set out in the{" "}
        <a href="/privacy-policy">Privacy Policy</a> at that time. Advertisers
        remain responsible for the content of their own advertisements, and
        Acadly does not endorse the products, services or claims of any
        advertiser.
      </p>

      <h2>8. Reader submissions and feedback</h2>
      <p>
        When you send a message, correction, topic request or any other
        feedback to Acadly, you grant us a worldwide, royalty-free, perpetual
        licence to use, edit and publish that feedback in connection with
        operating and improving the Site. We will not publish your name or
        contact details without your permission. Please do not send us
        confidential material or information you do not have the right to
        share.
      </p>

      <h2>9. Availability of the Site</h2>
      <p>
        We work to keep the Site available and up to date, but we do not
        guarantee uninterrupted access. The Site may be unavailable from time
        to time for maintenance, updates or reasons outside our control.
        Articles may be updated, corrected or withdrawn at any time.
      </p>

      <h2>10. No warranty</h2>
      <p>
        The Site and all content on it are provided on an &quot;as is&quot;
        and &quot;as available&quot; basis, without any express or implied
        warranty. We do not warrant that the content is accurate, complete,
        current, error-free or fit for any particular purpose. University
        syllabi, exam patterns, grading rules and admission policies change
        from time to time; you must verify any time-sensitive information
        against an official source before you rely on it.
      </p>

      <h2>11. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by applicable law, Acadly and its
        author will not be liable for any indirect, incidental, special,
        consequential or punitive damages, or any loss of profits, revenue,
        data, opportunities or goodwill, arising out of or in connection with
        your use of the Site or your reliance on any content published on it,
        even if we have been advised of the possibility of such damages. In
        every case, our total aggregate liability arising out of or in
        connection with the Site will not exceed one thousand Indian rupees
        (INR 1,000).
      </p>
      <p>
        Nothing in these Terms limits or excludes any liability that cannot be
        limited or excluded under applicable law.
      </p>

      <h2>12. Indemnity</h2>
      <p>
        You agree to indemnify and hold Acadly and its author harmless from
        any claim, demand, loss or expense (including reasonable legal fees)
        that arises out of your breach of these Terms, your misuse of the
        Site or your violation of any applicable law or the rights of any
        third party.
      </p>

      <h2>13. Governing law and jurisdiction</h2>
      <p>
        These Terms are governed by and construed in accordance with the laws
        of India, without regard to its conflict-of-law rules. Subject to
        applicable law, any dispute arising out of or in connection with
        these Terms or your use of the Site shall be submitted to the
        exclusive jurisdiction of the competent courts at New Delhi, India.
      </p>

      <h2>14. Changes to these Terms</h2>
      <p>
        We may update these Terms from time to time. When we do, we will
        change the &quot;Last updated&quot; date at the top of this page and,
        where the change is significant, will highlight it on the Site for a
        reasonable period. Your continued use of the Site after an update
        constitutes acceptance of the revised Terms. If you do not accept the
        revised Terms, please stop using the Site.
      </p>

      <h2>15. Contact</h2>
      <p>
        Questions about these Terms can be sent to{" "}
        <a href="mailto:hello@acadly.in">hello@acadly.in</a> or through the{" "}
        <a href="/contact">contact page</a>.
      </p>
    </div>
  );
}